import * as React from 'react';

import {
  GetProductsByIdBatchedQuery,
  GetProductsByIdBatchedQueryVariables,
} from '@notino/shared/definitions/types';

import getProductsByIdBatchedQuery from '@components/Universals/BatchVPProductQuery/queries/productsByIdBatched.graphql';
import { dispatchProductsShownEvent } from '@containers/ProductDetailContainer/ProductDetail/hooks/useDispatchProductsShowEvent';
import { subtractArrays } from '@utils/array';

import { trackWishlistProductListImpression } from './tracking';
import useLazyQueryWorkaround from './useLazyQueryWorkaround';
import { useRemoveNullWishlistProducts } from './useRemoveNullWishlistProducts';
import { useSsrWishlistProducts } from './useSsrWishlistProducts';
import { findNullProductIds } from './utils';

const usePagination = () =>
  React.useReducer((prevPage: number) => prevPage + 1, 0);

export const useWishlistedProducts = (
  productIds: string[],
  isSharedWishlist: boolean,
  { pageSize }: { pageSize: number }
) => {
  const [page, nextPage] = usePagination();
  const removeNullProducts = useRemoveNullWishlistProducts(!isSharedWishlist);

  const [products, setProducts] = useSsrWishlistProducts({
    productIds,
    pageSize,
  });
  const alreadyFetchedIds = React.useRef<string[]>(
    products.length ? productIds.slice(0, pageSize) : []
  );

  const [fetchMore, { loading, error }] = useLazyQueryWorkaround<
    GetProductsByIdBatchedQuery,
    GetProductsByIdBatchedQueryVariables
  >(getProductsByIdBatchedQuery, (response, { variables }) => {
    const variableIds = Array.isArray(variables.ids)
      ? variables.ids
      : [variables.ids];
    alreadyFetchedIds.current = [...alreadyFetchedIds.current, ...variableIds];

    const allNewProducts = response?.vpProductByIds ?? [];
    const nonNullProducts = allNewProducts.filter(Boolean);

    if (nonNullProducts.length > 0) {
      trackWishlistProductListImpression(
        nonNullProducts,
        productIds,
        page === 0 ? 'automatic' : 'button'
      );
      dispatchProductsShownEvent(nonNullProducts, {
        section: 'Wishlist',
      });
    }

    setProducts((prev) => [...prev, ...nonNullProducts]);

    if (allNewProducts.length !== nonNullProducts.length) {
      removeNullProducts(findNullProductIds(variableIds, nonNullProducts));
    }
  });

  /**
   * this useEffect is used when user removes item from wishlist,
   * so product list is refreshed with new items
   */
  React.useEffect(() => {
    setProducts((prev) => prev.filter((prod) => productIds.includes(prod.id)));
  }, [productIds, setProducts]);

  /**
   * this useEffect handles fetching of new products when page or productIds change
   *  when page change => classic pagination
   *  when productIds list change => this occurs on remove from wishlist, so fetch items to the end of the page
   */
  React.useEffect(() => {
    const endOfCurrentPage = (page + 1) * pageSize;
    const idsThatShouldBeLoaded = productIds.slice(0, endOfCurrentPage);

    const productIdsToFetch = subtractArrays(
      idsThatShouldBeLoaded,
      alreadyFetchedIds.current
    );

    if (productIdsToFetch.length > 0) {
      void fetchMore({
        variables: {
          ids: productIdsToFetch,
        },
      });
    }
  }, [productIds, pageSize, fetchMore, page, products]);

  const setProductWishlisted = React.useCallback(
    (productId: string, wishlisted: boolean) => {
      setProducts((prev) =>
        prev.map((product) =>
          product.id === productId ? { ...product, wishlisted } : product
        )
      );
    },
    [setProducts]
  );

  return {
    products,
    fetchMore: nextPage,
    loading,
    error,
    hasMore: products.length < productIds.length,
    isEmpty: products.length === 0,
    setProductWishlisted,
  } as const;
};
